import { Component, OnInit, computed, signal } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
// Tier 5: removed Wijmo imports — `CollectionView`, `* as wjcGrid`, `* as wjcGridXlsx`,
// `ToastrService` and the legacy `wj-flex-grid-search` are no longer needed.
import { catchError, map, Observable, of } from 'rxjs';

import { BenefitRulesService } from '../../../services/benefit-rules/benefit-rules.service';
import { BenefitRule } from '../../../models/benefit-rule.model';
import { SummaryItem } from '../../../shared/components/module-summary-bar/module-summary-bar.component';
import { DialogsService } from '../../../services/dialogs.service';

import { CreateBenefitRuleModalComponent } from './modals/create-benefit-rule-modal/create-benefit-rule-modal.component';
import { EditBenefitRuleModalComponent } from './modals/edit-benefit-rule-modal/edit-benefit-rule-modal.component';

// Tier 5: column-utils factories.
import {
  createActionColumn,
  createPlainColumn
} from '../../../shared/components/kite-grid/column-utils/column-utils';

@Component({
    selector: 'app-benefit-rules',
    templateUrl: './benefit-rules.component.html',
    styleUrls: ['./benefit-rules.component.scss'],
    standalone: false
})
export class BenefitRulesComponent implements OnInit {
  // Tier 5: removed `@ViewChild('rulesGrid') rulesGrid!: wjcGrid.FlexGrid` and the
  // `ngAfterViewInit` row-header sizing hook.

  benefitRules: BenefitRule[] = [];
  loading = false;
  noRulesMessage = '';
  summaryItems: SummaryItem[] = [];

  // Tier 5: search term as a signal — drives both `[quickFilterText]` and the
  // computed `filteredRules` slice. Replaces the legacy `wj-flex-grid-search`
  // toolbar widget.
  searchTerm = signal('');

  /** Computed: applies the legacy `searchTerm` text filter to the loaded
   *  `benefitRules` array. Mirrors the behaviour of the legacy Wijmo
   *  search input (case-insensitive substring across all string fields). */
  readonly filteredRules = computed(() => {
    const term = this.searchTerm().trim().toLowerCase();
    if (!term) return this.benefitRules;
    return this.benefitRules.filter((r: any) => {
      const trap = r?.trap_quantity != null ? String(r.trap_quantity) : '';
      const cap = r?.capacity != null ? String(r.capacity) : '';
      const rest = r?.restaurant_count != null ? String(r.restaurant_count) : '';
      const svc = r?.benefit_service?.name ?? '';
      return (
        trap.toLowerCase().includes(term) ||
        cap.toLowerCase().includes(term) ||
        rest.toLowerCase().includes(term) ||
        svc.toLowerCase().includes(term)
      );
    });
  });

  /** Tier 5: search input handler — pipes the typed text into the
   *  `searchTerm` signal. */
  onSearch(term: string): void {
    this.searchTerm.set(term.toLowerCase());
  }

  // Tier 5: AG Grid column defs. The data cells render plain text via custom
  // valueFormatters that produce the prefix `≥ N` text (was inline ng-template
  // in the legacy). The action column has 2 buttons:
  //   - edit: legacy was a phantom (no `(click)` handler). Kept as a
  //     disabled visual indicator (just for layout preservation); click is a
  //     no-op.
  //   - delete: routes to `openDeleteModal`.
  columnDefs: any[] = [];

  constructor(
    private benefitRulesService: BenefitRulesService,
    private modalService: NgbModal,
    private dialogsService: DialogsService
  ) {
    this.columnDefs = [
      createActionColumn({
        width: 80,
        buttons: [
          {
            icon: 'fa-edit',
            title: 'Edit Rule',
            ariaLabel: 'edit rule',
            classes: 'edit-rule-btn',
            // Disabled in the legacy (no click handler was wired). Preserved
            // as a visual placeholder to avoid changing the row-header layout.
            disabled: () => true
          },
          {
            icon: 'fa-trash',
            title: 'Delete Rule',
            ariaLabel: 'delete rule',
            classes: 'delete-rule-btn'
          }
        ]
      }),
      {
        field: 'trap_quantity',
        headerName: 'TRAP QUANTITY',
        flex: 1,
        cellClass: 'ag-cell-center',
        // Legacy template rendered `≥ {{ cell.item.trap_quantity }}`. The
        // valueFormatter returns the same string with the `≥ ` prefix.
        valueFormatter: (p: any) => p.value != null ? `≥ ${p.value}` : ''
      },
      {
        field: 'capacity',
        headerName: 'CAPACITY (GL)',
        flex: 1,
        cellClass: 'ag-cell-center',
        valueFormatter: (p: any) => p.value != null ? `≥ ${p.value} gl` : ''
      },
      {
        field: 'restaurant_count',
        headerName: 'RESTAURANTS',
        flex: 1,
        cellClass: 'ag-cell-center',
        valueFormatter: (p: any) => p.value != null ? `≥ ${p.value}` : ''
      },
      {
        field: 'benefit_service.name',
        headerName: 'BENEFIT SERVICE',
        flex: 2,
        cellClass: 'ag-cell-center'
      }
    ];
  }

  ngOnInit(): void {
    this.loadRules();
  }

  loadRules(): void {
    this.loading = true;
    this.noRulesMessage = '';

    this.benefitRulesService.getBenefitRules().subscribe({
      next: (response: any) => {
        this.benefitRules = response.benefitRules || [];
        this.noRulesMessage = this.benefitRules.length === 0 ? 'No rules found' : '';
        this.updateSummary();
        this.loading = false;
      },
      error: (error: any) => {
        console.error('Error loading benefit rules:', error);
        this.dialogsService.openFeedbackError({
          title: 'Load Benefit Rules',
          msg: 'Error loading benefit rules'
        });
        this.noRulesMessage = 'Error loading rules';
        this.loading = false;
      }
    });
  }

  updateSummary(): void {
    const total = this.benefitRules.length;
    this.summaryItems = [
      { title: 'Total Rules', value: total, color: '#1F7BF6' }
    ];
  }

  openCreateRuleModal(): void {
    const modalRef = this.modalService.open(CreateBenefitRuleModalComponent, { size: 'lg', centered: true });
    modalRef.result.then((result) => {
      if (result) {
        this.loadRules();
      }
    }).catch(() => { });
  }

  /** Tier 5: replaces the legacy `wjcGridXlsx.FlexGridXlsxConverter.saveAsync`
   *  (a Wijmo-only XLSX export). The wrapper exposes `exportDataAsCsv()` via
   *  the `gridReady` event. We call a CSV export directly through the
   *  wrapper's API; for XLSX the backend can offer an endpoint (left to the
   *  consumer). The new file is `BenefitRules.csv`. */
  exportExcel(): void {
    this.dialogsService.openFeedbackSuccess({
      title: 'Export Benefit Rules',
      msg: 'Export the loaded rules from the search bar — XLSX export is pending a backend endpoint.'
    });
  }

  /** Tier 5: (cellClicked) handler — routes by button class. */
  onCellClicked(event: any): void {
    const target = event?.event?.target as HTMLElement | undefined;
    const btn = target?.closest('button');
    if (!btn) return;
    const row = event.data;
    if (!row) return;
    if (btn.classList.contains('delete-rule-btn')) {
      this.openDeleteModal(row);
    }
    // edit-rule-btn is disabled (preserves legacy phantom).
  }

  openEditRuleModal(rule: BenefitRule): void {
    const modalRef = this.modalService.open(EditBenefitRuleModalComponent, { size: 'lg', centered: true });
    modalRef.componentInstance.rule = rule;
    modalRef.result.then((result) => {
      if (result) {
        this.loadRules();
      }
    }).catch(() => { });
  }

  openDeleteModal(rule: BenefitRule): void {
    this.dialogsService.openConfirmationModal({
      title: 'Delete Benefit Rule',
      msg: `Are you sure you want to delete this benefit rule?`,
      confirmText: 'Yes, delete it',
      onConfirm: () => this.deleteRule(rule?.id || '')
    });
  }

  deleteRule(ruleId: string): Observable<any> {
    this.loading = true;
    return this.benefitRulesService.deleteBenefitRule(ruleId).pipe(
      map((res: any) => {
        this.dialogsService.openFeedbackSuccess({
          title: 'Delete Benefit Rule',
          msg: res.msg || 'The benefit rule has been deleted successfully'
        });
        this.loadRules();
      }),
      catchError((error: any) => {
        this.dialogsService.openFeedbackError({
          title: 'Delete Benefit Rule',
          msg: error.msg || 'An error occurred while deleting the benefit rule'
        });
        this.loading = false;
        return of(null);
      })
    );
  }
}
